import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface LinkDialogInsertData {
  url: string;
  text: string;
  target: string;
}

export interface LinkDialogProps {
  open: boolean;
  initialUrl?: string;
  initialText?: string;
  initialTarget?: string;
  isEdit?: boolean;
  theme?: string;
  onInsert: (data: LinkDialogInsertData) => void;
  onCancel: () => void;
}

export function LinkDialog({
  open,
  initialUrl = '',
  initialText = '',
  initialTarget = '_self',
  isEdit = false,
  theme: themeProp,
  onInsert,
  onCancel,
}: LinkDialogProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [target, setTarget] = useState('_self');
  const [urlError, setUrlError] = useState('');

  const urlInputRef = useRef<HTMLInputElement>(null);

  const validateUrl = useCallback((value: string): string => {
    if (!value.trim()) {
      return '';
    }
    // Basic URL validation - allow relative URLs, http, https, mailto, tel
    const urlPattern = /^(https?:\/\/|mailto:|tel:|\/|#)/i;
    if (!urlPattern.test(value.trim()) && value.indexOf('.') === -1) {
      return 'Please enter a valid URL';
    }
    return '';
  }, []);

  const isValid = useMemo(() => {
    return url.trim().length > 0 && !urlError;
  }, [url, urlError]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setText(initialText);
      setTarget(initialTarget);
      setUrlError('');
    }
  }, [open, initialUrl, initialText, initialTarget]);

  // Focus URL input when dialog opens
  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => {
        urlInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  // Validate URL on change
  useEffect(() => {
    setUrlError(validateUrl(url));
  }, [url, validateUrl]);

  const handleInsert = useCallback(() => {
    if (!isValid) return;

    let finalUrl = url.trim();
    // Add https:// if no protocol and looks like a domain
    if (!finalUrl.match(/^(https?:\/\/|mailto:|tel:|\/|#)/i)) {
      finalUrl = 'https://' + finalUrl;
    }

    onInsert({
      url: finalUrl,
      text: text.trim() || finalUrl,
      target,
    });
    onCancel();
  }, [isValid, url, text, target, onInsert, onCancel]);

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleInsert();
      }
    },
    [handleClose, handleInsert]
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!open) return null;

  const dialogClasses = [
    'kst-dialog',
    'kst-link-dialog',
    theme || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="kst-dialog-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeydown}
    >
      <div className={dialogClasses} role="dialog" aria-labelledby="link-dialog-title">
        <div className="kst-dialog-header">
          <h2 id="link-dialog-title">{isEdit ? 'Edit Link' : 'Insert Link'}</h2>
          <button className="kst-dialog-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="kst-dialog-body">
          <div className="kst-form-group">
            <label htmlFor="link-url">
              URL <span className="required">*</span>
            </label>
            <input
              ref={urlInputRef}
              id="link-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className={urlError ? 'has-error' : ''}
            />
            {urlError && <span className="error-message">{urlError}</span>}
          </div>
          <div className="kst-form-group">
            <label htmlFor="link-text">Display Text</label>
            <input
              id="link-text"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Link text (optional)"
            />
          </div>
          <div className="kst-form-group">
            <label htmlFor="link-target">Open In</label>
            <select
              id="link-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              <option value="_self">Same Window</option>
              <option value="_blank">New Window</option>
            </select>
          </div>
        </div>
        <div className="kst-dialog-footer">
          <button className="kst-btn kst-btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="kst-btn kst-btn-primary"
            onClick={handleInsert}
            disabled={!isValid}
          >
            {isEdit ? 'Update' : 'Insert'}
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).KstLinkDialogReact = LinkDialog;
